import { z } from 'zod'

// Statistical reports are filed outside the payroll software, so operators
// confirm each one by hand once the month is closed. Order is display order.
export const statisticalReportValues = [
  'earnings_survey',
  'severe_disability_notice',
] as const

export const StatisticalReportEnum = z.enum(statisticalReportValues)

export type StatisticalReport = (typeof statisticalReportValues)[number]

interface StatisticalReportRule {
  // Calendar months (1-12) whose close makes the report due; null = every month.
  dueAfterMonths: readonly number[] | null
  // Month and day of the deadline in the year after the closed month.
  deadlineNextYear: { month: number; day: number } | null
}

const statisticalReportRules: Record<StatisticalReport, StatisticalReportRule> =
  {
    // Monatliche Verdiensterhebung: can only be created in Agenda after the close.
    earnings_survey: { dueAfterMonths: null, deadlineNextYear: null },
    // Schwerbehindertenanzeige: yearly, for the full year, due by 31 March.
    severe_disability_notice: {
      dueAfterMonths: [12],
      deadlineNextYear: { month: 3, day: 31 },
    },
  }

const monthOf = (yearMonth: string) => Number(yearMonth.slice(5, 7))

export function dueStatisticalReports(
  reports: readonly StatisticalReport[] | null | undefined,
  yearMonth: string
): StatisticalReport[] {
  if (!reports?.length) return []
  return statisticalReportValues.filter((report) => {
    if (!reports.includes(report)) return false
    const { dueAfterMonths } = statisticalReportRules[report]
    return (
      dueAfterMonths === null || dueAfterMonths.includes(monthOf(yearMonth))
    )
  })
}

/** Deadline as YYYY-MM-DD, or null when the report has no fixed deadline. */
export function statisticalReportDeadline(
  report: StatisticalReport,
  yearMonth: string
): string | null {
  const deadline = statisticalReportRules[report].deadlineNextYear
  if (!deadline) return null
  const year = Number(yearMonth.slice(0, 4)) + 1
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${year}-${pad(deadline.month)}-${pad(deadline.day)}`
}
